import React from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import {
  OUTSTANDING_PAYMENT_NOTE,
  atPeriodEndPhrase,
  formatMonthlyPrice,
  periodEndOrFallback,
  type BillingSummary,
} from './billingModel';
import type { BillingNotice, ConfirmRequest, PendingPlanChange } from './useBillingActions';

/**
 * The confirmations every billing change goes through. Their wording is the
 * contract with the customer (what is charged, when, and what they keep), so
 * change it only together with the billing rules it describes.
 */

interface ErrorProps {
  error?: BillingNotice | null;
  onDismissError?: () => void;
}

const DialogError: React.FC<ErrorProps> = ({ error, onDismissError }) =>
  error ? (
    <Alert severity={error.severity} onClose={onDismissError} sx={{ mb: 2, whiteSpace: 'pre-line' }}>
      {error.message}
    </Alert>
  ) : null;

/** A yes/no question raised from a handler (keep the plan, Moneris downgrades). */
export const ConfirmDialog: React.FC<{ request: ConfirmRequest | null; onSettle: (confirmed: boolean) => void } & ErrorProps> = ({
  request,
  onSettle,
  error,
  onDismissError,
}) => (
  <Dialog open={Boolean(request)} onClose={() => onSettle(false)} maxWidth="xs" fullWidth>
    <DialogTitle>{request?.title}</DialogTitle>
    <DialogContent>
      <DialogError error={error} onDismissError={onDismissError} />
      <Typography variant="body2" color="text.secondary">
        {request?.body}
      </Typography>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2.5 }}>
      <Button onClick={() => onSettle(false)} color="inherit">
        Cancel
      </Button>
      <Button variant="contained" onClick={() => onSettle(true)}>
        {request?.confirmLabel}
      </Button>
    </DialogActions>
  </Dialog>
);

/** Stripe upgrade (charges the card on file today) or downgrade (at period end). */
export const PlanChangeConfirmDialog: React.FC<
  {
    pending: PendingPlanChange | null;
    summary: BillingSummary;
    busy: boolean;
    onCancel: () => void;
    onConfirm: () => void;
  } & ErrorProps
> = ({ pending, summary, busy, onCancel, onConfirm, error, onDismissError }) => {
  const price = pending ? formatMonthlyPrice(pending.price, summary.currency) : '';
  const title = !pending
    ? ''
    : pending.kind === 'upgrade'
      ? `Upgrade to ${pending.planName}?`
      : summary.periodEndLabel
        ? `Switch to ${pending.planName} on ${summary.periodEndLabel}?`
        : `Switch to ${pending.planName} at the end of your billing period?`;

  return (
    <Dialog open={Boolean(pending)} onClose={() => !busy && onCancel()} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogError error={error} onDismissError={onDismissError} />
        {pending?.kind === 'upgrade' ? (
          <>
            <Typography variant="body2" gutterBottom>
              {pending.planName} is {price} plus applicable tax.
            </Typography>
            <Typography variant="body2" gutterBottom>
              Your card on file is charged today for the prorated difference for the rest of the current billing period,
              plus tax.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {summary.periodEndLabel
                ? `Your next billing date is ${summary.periodEndLabel}.`
                : 'Your next billing date does not change.'}
            </Typography>
          </>
        ) : pending ? (
          <>
            <Typography variant="body2" gutterBottom>
              You keep {pending.currentPlanName} until then. No refund or credit is issued.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              After that, {pending.planName} is {price} plus applicable tax.
            </Typography>
          </>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onCancel} disabled={busy} color="inherit">
          Cancel
        </Button>
        <Button variant="contained" onClick={onConfirm} disabled={busy}>
          {busy ? 'Working…' : pending?.kind === 'upgrade' ? 'Upgrade and pay now' : 'Schedule switch'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface FreeLimitsProps {
  /** The Free plan's name ("Free"). */
  freePlanName: string;
  /** "1 GB, 3 active projects": what someone leaving a paid plan must fit in before adding more. */
  freeLimitsLabel: string;
}

/** Paid plan -> Free: ends the paid subscription at period end. */
export const SwitchToFreeDialog: React.FC<
  { open: boolean; summary: BillingSummary; busy: boolean; onKeep: () => void; onConfirm: () => void } & FreeLimitsProps &
    ErrorProps
> = ({ open, summary, busy, onKeep, onConfirm, freePlanName, freeLimitsLabel, error, onDismissError }) => (
  <Dialog open={open} onClose={() => !busy && onKeep()} maxWidth="xs" fullWidth>
    <DialogTitle>
      {summary.periodEndLabel
        ? `Switch to Free on ${summary.periodEndLabel}?`
        : 'Switch to Free at the end of your billing period?'}
    </DialogTitle>
    <DialogContent>
      <DialogError error={error} onDismissError={onDismissError} />
      <Typography variant="body2" gutterBottom>
        {summary.hasPaymentIssue
          ? `You keep ${summary.planName} until then. ${OUTSTANDING_PAYMENT_NOTE}`
          : `You keep ${summary.planName} until then and won't be charged again.`}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        If you&apos;re over {freePlanName}&apos;s limits ({freeLimitsLabel}) you can keep everything, but you won&apos;t be
        able to add more until you&apos;re under them.
      </Typography>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2.5 }}>
      <Button onClick={onKeep} disabled={busy} color="inherit">
        Keep {summary.planName}
      </Button>
      <Button variant="contained" onClick={onConfirm} disabled={busy}>
        {busy ? 'Working…' : 'Switch to Free'}
      </Button>
    </DialogActions>
  </Dialog>
);

/** Cancel at period end: paid up until then, then Free; nothing is deleted. */
export const CancelSubscriptionDialog: React.FC<
  {
    open: boolean;
    summary: BillingSummary;
    busy: boolean;
    disabled?: boolean;
    onKeep: () => void;
    onConfirm: () => void;
  } & FreeLimitsProps &
    ErrorProps
> = ({ open, summary, busy, disabled, onKeep, onConfirm, freePlanName, freeLimitsLabel, error, onDismissError }) => {
  const endDate = periodEndOrFallback(summary);
  const items = summary.planIsFree
    ? [`Billing stops after ${endDate}`, 'Your account stays on the Free plan']
    : [
        `You'll move to Free ${atPeriodEndPhrase(summary)}`,
        `If you're over ${freePlanName}'s limits (${freeLimitsLabel}) you keep everything, but you can't add more until you're under them`,
      ];

  return (
    <Dialog open={open} onClose={() => !busy && onKeep()} maxWidth="sm" fullWidth>
      <DialogTitle>Cancel your subscription?</DialogTitle>
      <DialogContent>
        <DialogError error={error} onDismissError={onDismissError} />
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          {summary.hasPaymentIssue
            ? summary.planIsFree
              ? `Your subscription ends ${atPeriodEndPhrase(summary)}. ${OUTSTANDING_PAYMENT_NOTE}`
              : `You keep ${summary.planName} until ${endDate}, then move to Free. ${OUTSTANDING_PAYMENT_NOTE}`
            : summary.planIsFree
              ? `Your subscription stays paid up until ${endDate}, and you won't be charged after that.`
              : `You keep ${summary.planName} until ${endDate}, then move to Free. You won't be charged again.`}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          After cancellation:
        </Typography>
        <Box component="ul" sx={{ mt: 0.75, mb: 0, pl: 2.5 }}>
          {[...items, 'Your data and projects are kept', 'You can subscribe again at any time'].map((item) => (
            <Typography component="li" variant="body2" color="text.secondary" key={item} sx={{ mb: 0.5 }}>
              {item}
            </Typography>
          ))}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onKeep} disabled={busy} color="inherit">
          Keep subscription
        </Button>
        <Button variant="contained" color="error" onClick={onConfirm} disabled={busy || disabled}>
          {busy ? 'Cancelling…' : 'Yes, cancel subscription'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
